import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import type { EChartsCoreOption } from 'echarts/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvLoadingItemComponent } from '../../../../Shared/Components/av-loading-item/av-loading-item.component';
import { AvTableColumn, AvTableRow } from '../../../../Shared/Interfaces/av-table-card.interface';
import { AnomaliesReportFacade } from '../../../../../Facades/reports/anomalies.facade';
import { ThemeService } from '../../../../Shared/Services/theme.service';
import { exportAllToCsv } from '../../../../Shared/Utils/csv-export.utils';
import { ReportKpiTileComponent } from '../../Partials/report-kpi-tile/report-kpi-tile.component';
import { ReportChartCardComponent } from '../../Partials/report-chart-card/report-chart-card.component';
import { ReportDetailDialogComponent } from '../../Dialogs/report-detail-dialog/report-detail-dialog.component';
import { ReportDetailDialogData, ReportDetailSection } from '../../../models/report-dashboard.types';
import {
  categoryAxis,
  CHART_ACCENT_GREEN,
  CHART_CATEGORY_ACCENTS,
  donutSeriesDefaults,
  integerValueAxis,
  legendBottom,
  readChartThemePalette,
} from '../../../utils/chart-theme.utils';
import { EM_DASH } from '../../../Consts/em-dash.const';
import { TILE_SEVERITY } from '../../../Consts/anomalies-tile-severity.const';

@Component({
  selector: 'app-anomalies-report',
  standalone: true,
  imports: [COMMON_IMPORTS, NgxEchartsDirective, AvLoadingItemComponent, ReportKpiTileComponent, ReportChartCardComponent],
  templateUrl: './anomalies-report.component.html',
  styleUrl: './anomalies-report.component.scss',
})
export class AnomaliesReportComponent {
  readonly facade = inject(AnomaliesReportFacade);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly themeService = inject(ThemeService);
  private readonly translationsReady = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly emDash = EM_DASH;

  private readonly appFailureColumns: AvTableColumn[] = [
    { key: 'DisplayName', label: 'app.reports.anomalies.columns.DisplayName', sortable: true },
    { key: 'Publisher', label: 'app.reports.anomalies.columns.Publisher', sortable: true },
    { key: 'Platform', label: 'app.reports.anomalies.columns.Platform', sortable: true },
    { key: 'AppVersion', label: 'app.reports.anomalies.columns.AppVersion', sortable: true },
    { key: 'FailedDeviceCount', label: 'app.reports.anomalies.columns.FailedDeviceCount', sortable: true },
    { key: 'FailedUserCount', label: 'app.reports.anomalies.columns.FailedUserCount', sortable: true },
    { key: 'FailedDevicePercentage', label: 'app.reports.anomalies.columns.FailedDevicePercentage', sortable: true },
  ];

  private readonly multipleUsersColumns: AvTableColumn[] = [
    { key: 'deviceName', label: 'app.reports.anomalies.columns.deviceName', sortable: true },
    { key: 'primaryUser', label: 'app.reports.anomalies.columns.primaryUser', sortable: true },
    { key: 'enrollmentProfile', label: 'app.reports.anomalies.columns.enrollmentProfile', sortable: true },
    { key: 'usersLoggedOnCount', label: 'app.reports.anomalies.columns.usersLoggedOnCount', sortable: true },
    { key: 'usersLoggedOnIds', label: 'app.reports.anomalies.columns.usersLoggedOnIds' },
  ];

  private readonly deviceColumns: AvTableColumn[] = [
    { key: 'deviceName', label: 'app.reports.anomalies.columns.deviceName', sortable: true },
    { key: 'userPrincipalName', label: 'app.reports.anomalies.columns.userPrincipalName', sortable: true },
    { key: 'manufacturer', label: 'app.reports.anomalies.columns.manufacturer', sortable: true },
    { key: 'osVersion', label: 'app.reports.anomalies.columns.osVersion', sortable: true },
    { key: 'lastSyncDateTime', label: 'app.reports.anomalies.columns.lastSyncDateTime', sortable: true },
    { key: 'complianceState', label: 'app.reports.anomalies.columns.complianceState', sortable: true },
  ];

  private readonly autopilotColumns: AvTableColumn[] = [
    { key: 'deviceName', label: 'app.reports.anomalies.columns.deviceName', sortable: true },
    { key: 'serialNumber', label: 'app.reports.anomalies.columns.serialNumber', sortable: true },
    { key: 'userPrincipalName', label: 'app.reports.anomalies.columns.userPrincipalName', sortable: true },
    { key: 'manufacturer', label: 'app.reports.anomalies.columns.manufacturer', sortable: true },
  ];

  private readonly inactiveColumns: AvTableColumn[] = [
    { key: 'deviceName', label: 'app.reports.anomalies.columns.deviceName', sortable: true },
    { key: 'userPrincipalName', label: 'app.reports.anomalies.columns.userPrincipalName', sortable: true },
    { key: 'lastSyncDateTime', label: 'app.reports.anomalies.columns.lastSyncDateTime', sortable: true },
    { key: 'manufacturer', label: 'app.reports.anomalies.columns.manufacturer', sortable: true },
  ];

  private readonly osEditionColumns: AvTableColumn[] = [
    { key: 'deviceName', label: 'app.reports.anomalies.columns.deviceName', sortable: true },
    { key: 'userPrincipalName', label: 'app.reports.anomalies.columns.userPrincipalName', sortable: true },
    { key: 'operatingSystemEdition', label: 'app.reports.anomalies.columns.operatingSystemEdition', sortable: true },
    { key: 'osVersion', label: 'app.reports.anomalies.columns.osVersion', sortable: true },
    { key: 'manufacturer', label: 'app.reports.anomalies.columns.manufacturer', sortable: true },
  ];

  private readonly disabledUsersColumns: AvTableColumn[] = [
    { key: 'deviceName', label: 'app.reports.anomalies.columns.deviceName', sortable: true },
    { key: 'userPrincipalName', label: 'app.reports.anomalies.columns.userPrincipalName', sortable: true },
    { key: 'lastSyncDateTime', label: 'app.reports.anomalies.columns.lastSyncDateTime', sortable: true },
    { key: 'manufacturer', label: 'app.reports.anomalies.columns.manufacturer', sortable: true },
  ];

  private readonly atRiskColumns: AvTableColumn[] = [
    { key: 'deviceName', label: 'app.reports.dashboard.at_risk.columns.device_name', sortable: true },
    { key: 'penalty', label: 'app.reports.dashboard.at_risk.columns.penalty', sortable: true },
    { key: 'memberships', label: 'app.reports.dashboard.at_risk.columns.memberships' },
  ];

  private readonly appFailureRows = computed<AvTableRow[]>(() =>
    this.facade.appFailure().map(d => ({ ...d }))
  );

  private readonly multipleUsersRows = computed<AvTableRow[]>(() =>
    this.facade.multipleUsers().map(d => ({ ...d }))
  );

  private readonly notEncryptedRows = computed<AvTableRow[]>(() =>
    this.facade.notEncrypted().map(d => ({
      deviceName: d.deviceName, userPrincipalName: d.userPrincipalName,
      manufacturer: d.manufacturer, osVersion: d.osVersion,
      lastSyncDateTime: d.lastSyncDateTime, complianceState: d.complianceState,
    }))
  );

  private readonly noAutopilotHashRows = computed<AvTableRow[]>(() =>
    this.facade.noAutopilotHash().map(d => ({
      deviceName: d.deviceName, serialNumber: d.serialNumber,
      userPrincipalName: d.userPrincipalName, manufacturer: d.manufacturer,
    }))
  );

  private readonly inactiveDevicesRows = computed<AvTableRow[]>(() =>
    this.facade.inactiveDevices().map(d => ({
      deviceName: d.deviceName, userPrincipalName: d.userPrincipalName,
      lastSyncDateTime: d.lastSyncDateTime, manufacturer: d.manufacturer,
    }))
  );

  private readonly noncompliantRows = computed<AvTableRow[]>(() =>
    this.facade.noncompliant().map(d => ({
      deviceName: d.deviceName, userPrincipalName: d.userPrincipalName,
      manufacturer: d.manufacturer, osVersion: d.osVersion,
      lastSyncDateTime: d.lastSyncDateTime, complianceState: d.complianceState,
    }))
  );

  private readonly osEditionRows = computed<AvTableRow[]>(() =>
    this.facade.osEdition().map(d => ({
      deviceName: d.deviceName, userPrincipalName: d.userPrincipalName,
      operatingSystemEdition: d.operatingSystemEdition, osVersion: d.osVersion,
      manufacturer: d.manufacturer,
    }))
  );

  private readonly disabledUsersRows = computed<AvTableRow[]>(() =>
    this.facade.disabledUsers().map(d => ({
      deviceName: d.deviceName, userPrincipalName: d.userPrincipalName,
      lastSyncDateTime: d.lastSyncDateTime, manufacturer: d.manufacturer,
    }))
  );

  private readonly atRiskRows = computed<AvTableRow[]>(() =>
    this.facade.atRiskDevices().map(entry => ({
      deviceName: entry.deviceName,
      penalty: entry.penalty,
      memberships: entry.memberships.map(key => this.translate.instant(this.membershipTitleKey(key))).join(', '),
    }))
  );

  readonly sections: ReportDetailSection[] = [
    { key: 'appFailure', titleKey: 'app.reports.anomalies.app_failure', icon: 'warning', columns: this.appFailureColumns, rows: () => this.appFailureRows() },
    { key: 'multipleUsers', titleKey: 'app.reports.anomalies.multiple_users', icon: 'groups', columns: this.multipleUsersColumns, rows: () => this.multipleUsersRows() },
    { key: 'notEncrypted', titleKey: 'app.reports.anomalies.not_encrypted_devices', icon: 'lock_open', columns: this.deviceColumns, rows: () => this.notEncryptedRows() },
    { key: 'noAutopilotHash', titleKey: 'app.reports.anomalies.no_autopilot_hash', icon: 'settings', columns: this.autopilotColumns, rows: () => this.noAutopilotHashRows() },
    { key: 'inactiveDevices', titleKey: 'app.reports.anomalies.inactive_devices', icon: 'schedule', columns: this.inactiveColumns, rows: () => this.inactiveDevicesRows() },
    { key: 'noncompliant', titleKey: 'app.reports.anomalies.non_compliant_devices', icon: 'gpp_bad', columns: this.deviceColumns, rows: () => this.noncompliantRows() },
    { key: 'osEdition', titleKey: 'app.reports.anomalies.os_edition_overview', icon: 'computer', columns: this.osEditionColumns, rows: () => this.osEditionRows() },
    { key: 'disabledUsers', titleKey: 'app.reports.anomalies.disabled_primary_users', icon: 'person_off', columns: this.disabledUsersColumns, rows: () => this.disabledUsersRows() },
  ];

  readonly atRiskSection: ReportDetailSection = {
    key: 'atRisk',
    titleKey: 'app.reports.dashboard.at_risk.title',
    icon: 'warning',
    columns: this.atRiskColumns,
    rows: () => this.atRiskRows(),
  };

  readonly tiles = computed(() =>
    this.facade.bucketStats().map(stat => this.toTileViewModel(stat.key, stat.count, stat.percent))
  );

  private readonly _healthGaugeOptions = signal<EChartsCoreOption | null>(null);
  private readonly _complianceDonutOptions = signal<EChartsCoreOption | null>(null);
  private readonly _categoryBarOptions = signal<EChartsCoreOption | null>(null);
  private readonly _osEditionDonutOptions = signal<EChartsCoreOption | null>(null);
  private readonly _topFailingAppsBarOptions = signal<EChartsCoreOption | null>(null);

  readonly healthGaugeOptions = this._healthGaugeOptions.asReadonly();
  readonly complianceDonutOptions = this._complianceDonutOptions.asReadonly();
  readonly categoryBarOptions = this._categoryBarOptions.asReadonly();
  readonly osEditionDonutOptions = this._osEditionDonutOptions.asReadonly();
  readonly topFailingAppsBarOptions = this._topFailingAppsBarOptions.asReadonly();

  constructor() {
    this.registerHealthGaugeEffect();
    this.registerComplianceDonutEffect();
    this.registerCategoryBarEffect();
    this.registerOsEditionDonutEffect();
    this.registerTopFailingAppsBarEffect();
  }

  openSection(key: string): void {
    const section = this.findSection(key);
    if (!section) return;
    this.dialog.open(ReportDetailDialogComponent, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      panelClass: 'report-detail-dialog-panel',
      data: {
        titleKey: section.titleKey,
        icon: section.icon,
        columns: section.columns,
        rows: section.rows(),
      } satisfies ReportDetailDialogData,
    });
  }

  membershipTitleKey(key: string): string {
    return this.findSection(key)?.titleKey ?? key;
  }

  exportAllCsv(): void {
    exportAllToCsv('anomalies-report', this.sections.map(section => ({
      title: this.translate.instant(section.titleKey),
      columns: section.columns,
      rows: section.rows(),
    })), key => this.translate.instant(key));
  }

  private findSection(key: string): ReportDetailSection | undefined {
    if (key === this.atRiskSection.key) return this.atRiskSection;
    return this.sections.find(section => section.key === key);
  }

  private toTileViewModel(key: string, count: number, percent: number | null) {
    const section = this.findSection(key);
    return {
      key,
      titleKey: section?.titleKey ?? key,
      icon: section?.icon ?? 'warning',
      count,
      percent,
      severity: TILE_SEVERITY[key] ?? 'neutral',
    };
  }

  private registerHealthGaugeEffect(): void {
    effect(() => {
      this.themeService.mode();
      this.translationsReady();
      this._healthGaugeOptions.set(this.buildHealthGaugeOptions());
    });
  }

  private buildHealthGaugeOptions(): EChartsCoreOption | null {
    const score = this.facade.healthScore();
    if (score === null) return null;
    const palette = readChartThemePalette();
    const bandColor = score < 50 ? palette.error : score < 80 ? palette.warning : CHART_ACCENT_GREEN;
    return {
      series: [
        {
          type: 'gauge',
          min: 0,
          max: 100,
          radius: '96%',
          startAngle: 220,
          endAngle: -40,
          progress: { show: true, width: 16, roundCap: true, itemStyle: { color: bandColor } },
          axisLine: { roundCap: true, lineStyle: { width: 16, color: [[1, palette.axis]], opacity: 0.4 } },
          pointer: { show: false },
          anchor: { show: false },
          title: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          detail: {
            valueAnimation: true,
            offsetCenter: [0, '2%'],
            fontSize: 44,
            fontWeight: 'bolder',
            color: bandColor,
            formatter: '{value}',
          },
          data: [{ value: score }],
        },
      ],
    };
  }

  private registerComplianceDonutEffect(): void {
    effect(() => {
      this.themeService.mode();
      this.translationsReady();
      this._complianceDonutOptions.set(this.buildComplianceDonutOptions());
    });
  }

  private buildComplianceDonutOptions(): EChartsCoreOption {
    const palette = readChartThemePalette();
    const data = this.facade.complianceDonutData().map(datum => ({
      ...datum,
      name: this.translate.instant(`app.reports.dashboard.compliance.${datum.name}`),
      itemStyle: { color: datum.key === 'noncompliant' ? palette.error : CHART_ACCENT_GREEN },
    }));
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      textStyle: { color: palette.text },
      legend: legendBottom(palette),
      series: [{ ...donutSeriesDefaults(), data }],
    };
  }

  private registerCategoryBarEffect(): void {
    effect(() => {
      this.themeService.mode();
      this.translationsReady();
      this._categoryBarOptions.set(this.buildCategoryBarOptions());
    });
  }

  private buildCategoryBarOptions(): EChartsCoreOption {
    const palette = readChartThemePalette();
    const data = this.facade.categoryBarData().map((datum, index) => ({
      ...datum,
      name: this.translate.instant(this.membershipTitleKey(datum.name)),
      itemStyle: {
        color: CHART_CATEGORY_ACCENTS[index % CHART_CATEGORY_ACCENTS.length],
        borderRadius: [0, 4, 4, 0],
      },
    }));
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 8, right: 24, top: 12, bottom: 8, containLabel: true },
      xAxis: integerValueAxis(palette),
      yAxis: categoryAxis(palette, data.map(d => d.name)),
      series: [{ type: 'bar', barMaxWidth: 22, data }],
    };
  }

  private registerOsEditionDonutEffect(): void {
    effect(() => {
      this.themeService.mode();
      this.translationsReady();
      this._osEditionDonutOptions.set(this.buildOsEditionDonutOptions());
    });
  }

  private buildOsEditionDonutOptions(): EChartsCoreOption {
    const palette = readChartThemePalette();
    const data = this.facade.osEditionDistribution().map((datum, index) => ({
      ...datum,
      itemStyle: { color: CHART_CATEGORY_ACCENTS[index % CHART_CATEGORY_ACCENTS.length] },
    }));
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      textStyle: { color: palette.text },
      legend: legendBottom(palette),
      series: [{ ...donutSeriesDefaults(), data }],
    };
  }

  private registerTopFailingAppsBarEffect(): void {
    effect(() => {
      this.themeService.mode();
      this.translationsReady();
      this._topFailingAppsBarOptions.set(this.buildTopFailingAppsBarOptions());
    });
  }

  private buildTopFailingAppsBarOptions(): EChartsCoreOption {
    const palette = readChartThemePalette();
    const data = this.facade.topFailingApps();
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 8, right: 24, top: 12, bottom: 8, containLabel: true },
      xAxis: integerValueAxis(palette),
      yAxis: categoryAxis(palette, data.map(d => d.name)),
      series: [{ type: 'bar', barMaxWidth: 22, data, itemStyle: { color: palette.primary, borderRadius: [0, 4, 4, 0] } }],
    };
  }
}
